import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Calendar as CalIcon,
    Inbox,
    LogOut,
    Truck,
    Clock,
    CheckCircle2,
    AlertTriangle,
    Mail,
    Trash2,
    X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, STATUS_OPTIONS, PACKAGE_TYPES, URGENCY_LEVELS } from "@/lib/constants";
import { Calendar } from "@/components/ui/calendar";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

function StatusBadge({ status }) {
    const opt = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
    return (
        <span
            data-testid={`status-badge-${status}`}
            className={`inline-block px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase border ${opt.color}`}
        >
            {opt.label}
        </span>
    );
}

function StatCard({ label, value, icon: Icon, accent }) {
    return (
        <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="bg-white border border-brand-line p-6 flex items-start justify-between">
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-black/60">
                    {label}
                </div>
                <div className="font-serif text-4xl text-brand-black mt-2">{value}</div>
            </div>
            <div className={`w-10 h-10 flex items-center justify-center ${accent}`}>
                <Icon size={18} />
            </div>
        </div>
    );
}

function pkgLabel(v) {
    return PACKAGE_TYPES.find((p) => p.value === v)?.label || v;
}
function urgLabel(v) {
    return URGENCY_LEVELS.find((u) => u.value === v)?.label || v;
}

export default function Admin() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [view, setView] = useState("dashboard");
    const [bookings, setBookings] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [stats, setStats] = useState(null);
    const [calDate, setCalDate] = useState(new Date());
    const [activeBooking, setActiveBooking] = useState(null);
    const [activeContact, setActiveContact] = useState(null);

    useEffect(() => {
        if (!loading && !user) navigate("/admin/login");
    }, [user, loading, navigate]);

    const loadAll = useCallback(async () => {
        try {
            const [b, c, s] = await Promise.all([
                api.get("/bookings"),
                api.get("/contact"),
                api.get("/admin/stats"),
            ]);
            setBookings(b.data);
            setContacts(c.data);
            setStats(s.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }, []);

    useEffect(() => {
        if (user) loadAll();
    }, [user, loadAll]);

    if (loading || !user)
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-cream">
                <div className="text-brand-black/50 text-xs tracking-[0.3em] uppercase">Loading…</div>
            </div>
        );

    const updateBookingStatus = async (id, newStatus) => {
        try {
            await api.patch(`/bookings/${id}`, { status: newStatus });
            toast.success("Status updated.");
            loadAll();
            if (activeBooking?.id === id) {
                setActiveBooking({ ...activeBooking, status: newStatus });
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const deleteBooking = async (id) => {
        if (!window.confirm("Delete this booking?")) return;
        try {
            await api.delete(`/bookings/${id}`);
            toast.success("Booking deleted.");
            setActiveBooking(null);
            loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const markContactRead = async (id, read) => {
        try {
            await api.patch(`/contact/${id}`, { is_read: read });
            loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const deleteContact = async (id) => {
        if (!window.confirm("Delete this message?")) return;
        try {
            await api.delete(`/contact/${id}`);
            toast.success("Message deleted.");
            setActiveContact(null);
            loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // bookings for selected calendar date
    const dateStr = (d) => {
        if (!d) return "";
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };
    const bookedDates = bookings
        .map((b) => {
            const [y, m, d] = b.pickup_date.split("-").map(Number);
            return new Date(y, m - 1, d);
        })
        .filter((d) => !isNaN(d));
    const selectedDayBookings = bookings.filter((b) => b.pickup_date === dateStr(calDate));

    const navItems = [
        { id: "dashboard", label: "Overview", icon: LayoutDashboard },
        { id: "bookings", label: "Bookings", icon: Truck },
        { id: "calendar", label: "Calendar", icon: CalIcon },
        { id: "inbox", label: "Inbox", icon: Inbox },
    ];

    return (
        <div data-testid="admin-page" className="min-h-screen flex bg-brand-cream">
            {/* Sidebar */}
            <aside className="w-64 bg-brand-black text-white flex flex-col">
                <div className="p-6 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-1.5">
                            <img src={LOGO_URL} alt="SheFleet" className="h-9 w-9 object-contain" />
                        </div>
                        <div>
                            <div className="font-serif text-lg leading-tight">SheFleet</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase text-brand-gold">
                                Dispatch
                            </div>
                        </div>
                    </div>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    {navItems.map((n) => {
                        const Icon = n.icon;
                        const active = view === n.id;
                        return (
                            <button
                                key={n.id}
                                data-testid={`nav-${n.id}`}
                                onClick={() => setView(n.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                                    active
                                        ? "bg-brand-gold/15 text-brand-gold border-l-2 border-brand-gold"
                                        : "text-white/70 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                                }`}
                            >
                                <Icon size={16} />
                                <span className="tracking-wide">{n.label}</span>
                                {n.id === "inbox" && stats?.unread_contacts > 0 && (
                                    <span className="ml-auto text-[10px] bg-brand-gold text-brand-black px-1.5 rounded-sm">
                                        {stats.unread_contacts}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>
                <div className="p-4 border-t border-white/10">
                    <div className="text-xs text-white/60 mb-3 truncate">{user.email}</div>
                    <button
                        data-testid="admin-logout"
                        onClick={async () => {
                            await logout();
                            navigate("/admin/login");
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-white/20 text-xs tracking-[0.2em] uppercase text-white hover:bg-brand-gold hover:text-brand-black hover:border-brand-gold transition-colors"
                    >
                        <LogOut size={14} /> Sign Out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 overflow-x-hidden">
                <header className="h-16 border-b border-brand-line bg-white px-8 flex items-center justify-between">
                    <div className="font-serif text-2xl text-brand-black capitalize">
                        {view === "dashboard" ? "Overview" : view}
                    </div>
                    <div className="text-xs tracking-[0.25em] uppercase text-brand-gold-deep">
                        {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                    </div>
                </header>

                <div className="p-8">
                    {view === "dashboard" && (
                        <div className="space-y-8" data-testid="dashboard-view">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <StatCard label="Total Bookings" value={stats?.total_bookings ?? 0} icon={Truck} accent="bg-brand-black text-brand-gold" />
                                <StatCard label="Pending" value={stats?.pending ?? 0} icon={Clock} accent="bg-amber-100 text-amber-800" />
                                <StatCard label="In Transit" value={stats?.in_transit ?? 0} icon={AlertTriangle} accent="bg-indigo-100 text-indigo-800" />
                                <StatCard label="Delivered" value={stats?.delivered ?? 0} icon={CheckCircle2} accent="bg-emerald-100 text-emerald-800" />
                            </div>

                            <div className="grid lg:grid-cols-2 gap-6">
                                <div className="bg-white border border-brand-line p-6">
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="font-serif text-xl">Recent Bookings</div>
                                        <button onClick={() => setView("bookings")} className="text-xs tracking-[0.2em] uppercase text-brand-gold-deep hover:underline">View all</button>
                                    </div>
                                    <div className="space-y-3">
                                        {bookings.slice(0, 5).map((b) => (
                                            <button
                                                key={b.id}
                                                onClick={() => setActiveBooking(b)}
                                                data-testid={`recent-booking-${b.id}`}
                                                className="w-full text-left flex items-center justify-between p-3 border border-brand-line hover:border-brand-gold transition-colors"
                                            >
                                                <div>
                                                    <div className="text-sm font-medium text-brand-black">{b.customer_name}</div>
                                                    <div className="text-xs text-brand-black/55">{b.pickup_date} · {b.pickup_time} · {pkgLabel(b.package_type)}</div>
                                                </div>
                                                <StatusBadge status={b.status} />
                                            </button>
                                        ))}
                                        {bookings.length === 0 && (
                                            <div className="text-sm text-brand-black/50 py-8 text-center">No bookings yet.</div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white border border-brand-line p-6">
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="font-serif text-xl">Recent Messages</div>
                                        <button onClick={() => setView("inbox")} className="text-xs tracking-[0.2em] uppercase text-brand-gold-deep hover:underline">View all</button>
                                    </div>
                                    <div className="space-y-3">
                                        {contacts.slice(0, 5).map((c) => (
                                            <button
                                                key={c.id}
                                                onClick={() => { setActiveContact(c); if (!c.is_read) markContactRead(c.id, true); }}
                                                data-testid={`recent-contact-${c.id}`}
                                                className="w-full text-left flex items-center justify-between p-3 border border-brand-line hover:border-brand-gold transition-colors"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-brand-black truncate">{c.subject}</div>
                                                    <div className="text-xs text-brand-black/55 truncate">{c.name} · {c.email}</div>
                                                </div>
                                                {!c.is_read && <span className="ml-3 w-2 h-2 rounded-full bg-brand-gold shrink-0" />}
                                            </button>
                                        ))}
                                        {contacts.length === 0 && (
                                            <div className="text-sm text-brand-black/50 py-8 text-center">No messages yet.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {view === "bookings" && (
                        <div className="bg-white border border-brand-line" data-testid="bookings-view">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-brand-line">
                                        <TableHead className="text-[10px] tracking-[0.2em] uppercase">Customer</TableHead>
                                        <TableHead className="text-[10px] tracking-[0.2em] uppercase">Pickup</TableHead>
                                        <TableHead className="text-[10px] tracking-[0.2em] uppercase">Drop-off</TableHead>
                                        <TableHead className="text-[10px] tracking-[0.2em] uppercase">Date / Time</TableHead>
                                        <TableHead className="text-[10px] tracking-[0.2em] uppercase">Package</TableHead>
                                        <TableHead className="text-[10px] tracking-[0.2em] uppercase">Urgency</TableHead>
                                        <TableHead className="text-[10px] tracking-[0.2em] uppercase">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {bookings.map((b) => (
                                        <TableRow
                                            key={b.id}
                                            data-testid={`booking-row-${b.id}`}
                                            onClick={() => setActiveBooking(b)}
                                            className="border-brand-line cursor-pointer hover:bg-brand-cream"
                                        >
                                            <TableCell>
                                                <div className="font-medium text-brand-black">{b.customer_name}</div>
                                                <div className="text-xs text-brand-black/55">{b.customer_email}</div>
                                            </TableCell>
                                            <TableCell className="max-w-xs truncate text-sm">{b.pickup_address}</TableCell>
                                            <TableCell className="max-w-xs truncate text-sm">{b.dropoff_address}</TableCell>
                                            <TableCell className="text-sm whitespace-nowrap">
                                                {b.pickup_date}
                                                <div className="text-xs text-brand-black/55">{b.pickup_time}</div>
                                            </TableCell>
                                            <TableCell className="text-sm">{pkgLabel(b.package_type)}</TableCell>
                                            <TableCell className="text-sm">{urgLabel(b.urgency)}</TableCell>
                                            <TableCell><StatusBadge status={b.status} /></TableCell>
                                        </TableRow>
                                    ))}
                                    {bookings.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-12 text-brand-black/50">
                                                No bookings yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}

                    {view === "calendar" && (
                        <div className="grid lg:grid-cols-12 gap-6" data-testid="calendar-view">
                            <div className="lg:col-span-5 bg-white border border-brand-line p-6">
                                <Calendar
                                    mode="single"
                                    selected={calDate}
                                    onSelect={(d) => d && setCalDate(d)}
                                    modifiers={{ booked: bookedDates }}
                                    modifiersClassNames={{
                                        booked: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-brand-gold",
                                    }}
                                    className="rounded-none"
                                />
                                <div className="mt-4 text-xs text-brand-black/60 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-brand-gold inline-block" />
                                    Days with scheduled pickups
                                </div>
                            </div>
                            <div className="lg:col-span-7 bg-white border border-brand-line p-6">
                                <div className="font-serif text-2xl text-brand-black mb-1">
                                    {calDate.toLocaleDateString(undefined, {
                                        weekday: "long",
                                        month: "long",
                                        day: "numeric",
                                        year: "numeric",
                                    })}
                                </div>
                                <div className="text-xs tracking-[0.25em] uppercase text-brand-gold-deep mb-6">
                                    {selectedDayBookings.length} scheduled
                                </div>
                                <div className="space-y-3 max-h-[520px] overflow-auto">
                                    {selectedDayBookings.map((b) => (
                                        <button
                                            key={b.id}
                                            onClick={() => setActiveBooking(b)}
                                            data-testid={`cal-booking-${b.id}`}
                                            className="w-full text-left p-4 border border-brand-line hover:border-brand-gold transition-colors"
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="font-medium text-brand-black">{b.pickup_time} · {b.customer_name}</div>
                                                <StatusBadge status={b.status} />
                                            </div>
                                            <div className="text-xs text-brand-black/55 mt-1">{pkgLabel(b.package_type)} · {urgLabel(b.urgency)}</div>
                                            <div className="text-xs text-brand-black/70 mt-2 truncate">{b.pickup_address} → {b.dropoff_address}</div>
                                        </button>
                                    ))}
                                    {selectedDayBookings.length === 0 && (
                                        <div className="text-sm text-brand-black/50 py-12 text-center border border-dashed border-brand-line">
                                            No pickups scheduled on this date.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {view === "inbox" && (
                        <div className="bg-white border border-brand-line" data-testid="inbox-view">
                            <div className="divide-y divide-brand-line">
                                {contacts.map((c) => (
                                    <button
                                        key={c.id}
                                        onClick={() => { setActiveContact(c); if (!c.is_read) markContactRead(c.id, true); }}
                                        data-testid={`contact-row-${c.id}`}
                                        className={`w-full text-left p-5 hover:bg-brand-cream transition-colors flex items-start gap-4 ${!c.is_read ? "bg-brand-gold/5" : ""}`}
                                    >
                                        <div className="mt-1">
                                            {!c.is_read ? (
                                                <span className="block w-2 h-2 rounded-full bg-brand-gold" />
                                            ) : (
                                                <Mail size={14} className="text-brand-black/30" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-3 mb-1">
                                                <div className={`text-sm ${!c.is_read ? "font-semibold text-brand-black" : "text-brand-black/85"}`}>
                                                    {c.subject}
                                                </div>
                                                <div className="text-xs text-brand-black/50 whitespace-nowrap">
                                                    {new Date(c.created_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <div className="text-xs text-brand-black/60 mb-2">{c.name} · {c.email}</div>
                                            <div className="text-sm text-brand-black/70 line-clamp-1">{c.message}</div>
                                        </div>
                                    </button>
                                ))}
                                {contacts.length === 0 && (
                                    <div className="p-16 text-center text-brand-black/50">No messages yet.</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Booking Detail Dialog */}
            <Dialog open={!!activeBooking} onOpenChange={(o) => !o && setActiveBooking(null)}>
                <DialogContent data-testid="booking-dialog" className="max-w-2xl rounded-none border-brand-line">
                    {activeBooking && (
                        <>
                            <DialogHeader>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-gold-deep mb-2">
                                    Booking #{activeBooking.id.slice(0, 8)}
                                </div>
                                <DialogTitle className="font-serif text-3xl">
                                    {activeBooking.customer_name}
                                </DialogTitle>
                                <DialogDescription className="sr-only">
                                    Booking details for {activeBooking.customer_name}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Email</div>
                                    <div>{activeBooking.customer_email}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Phone</div>
                                    <div>{activeBooking.customer_phone}</div>
                                </div>
                                {activeBooking.company && (
                                    <div className="col-span-2">
                                        <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Facility</div>
                                        <div>{activeBooking.company}</div>
                                    </div>
                                )}
                                <div className="col-span-2">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Pickup</div>
                                    <div>{activeBooking.pickup_address}</div>
                                </div>
                                <div className="col-span-2">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Drop-off</div>
                                    <div>{activeBooking.dropoff_address}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Date / Time</div>
                                    <div>{activeBooking.pickup_date} · {activeBooking.pickup_time}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Package</div>
                                    <div>{pkgLabel(activeBooking.package_type)}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Urgency</div>
                                    <div>{urgLabel(activeBooking.urgency)}</div>
                                </div>
                                {activeBooking.special_instructions && (
                                    <div className="col-span-2 bg-brand-cream p-3 border-l-2 border-brand-gold">
                                        <div className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mb-1">Special Instructions</div>
                                        <div className="text-sm">{activeBooking.special_instructions}</div>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-brand-line pt-4 mt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                                <div className="flex-1">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-black/60 mb-2">Update Status</div>
                                    <Select
                                        value={activeBooking.status}
                                        onValueChange={(v) => updateBookingStatus(activeBooking.id, v)}
                                    >
                                        <SelectTrigger data-testid="booking-status-select" className="rounded-none border-brand-black/20">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTIONS.map((s) => (
                                                <SelectItem key={s.value} value={s.value} data-testid={`status-opt-${s.value}`}>
                                                    {s.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <button
                                    onClick={() => deleteBooking(activeBooking.id)}
                                    data-testid="booking-delete"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-rose-300 text-rose-700 text-xs tracking-[0.2em] uppercase hover:bg-rose-50"
                                >
                                    <Trash2 size={14} /> Delete
                                </button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Contact Detail Dialog */}
            <Dialog open={!!activeContact} onOpenChange={(o) => !o && setActiveContact(null)}>
                <DialogContent data-testid="contact-dialog" className="max-w-xl rounded-none border-brand-line">
                    {activeContact && (
                        <>
                            <DialogHeader>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-gold-deep mb-2">
                                    Message · {new Date(activeContact.created_at).toLocaleString()}
                                </div>
                                <DialogTitle className="font-serif text-3xl">
                                    {activeContact.subject}
                                </DialogTitle>
                                <DialogDescription className="sr-only">
                                    Message from {activeContact.name}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <span className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mr-2">From</span>
                                    {activeContact.name} · <a className="text-brand-gold-deep underline" href={`mailto:${activeContact.email}`}>{activeContact.email}</a>
                                </div>
                                {activeContact.phone && (
                                    <div>
                                        <span className="text-[10px] tracking-[0.2em] uppercase text-brand-black/55 mr-2">Phone</span>
                                        {activeContact.phone}
                                    </div>
                                )}
                                <div className="bg-brand-cream p-4 border-l-2 border-brand-gold whitespace-pre-wrap text-brand-black/80">
                                    {activeContact.message}
                                </div>
                            </div>
                            <div className="border-t border-brand-line pt-4 flex items-center justify-between gap-3">
                                <button
                                    onClick={() => markContactRead(activeContact.id, !activeContact.is_read)}
                                    data-testid="contact-mark-read"
                                    className="px-5 py-2.5 border border-brand-black/20 text-xs tracking-[0.2em] uppercase hover:bg-brand-black hover:text-white transition-colors"
                                >
                                    Mark as {activeContact.is_read ? "Unread" : "Read"}
                                </button>
                                <button
                                    onClick={() => deleteContact(activeContact.id)}
                                    data-testid="contact-delete"
                                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-rose-300 text-rose-700 text-xs tracking-[0.2em] uppercase hover:bg-rose-50"
                                >
                                    <Trash2 size={14} /> Delete
                                </button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
